import { useCallback, useEffect, useState } from 'react'
import { deployedBuild, isNewer, refreshApp, type DeployedBuild } from '../lib/update'

/**
 * How often to look while the app simply sits open.
 *
 * Deliberately lazy. The check that MATTERS is the one on returning to the app,
 * because that is when a deploy which happened while it was closed becomes
 * relevant. The timer only exists for the phone left on a counter all evening,
 * and it costs one small static request.
 */
const CHECK_EVERY_MS = 5 * 60_000

export interface UpdateApi {
  available: DeployedBuild | null
  reload: () => void
  dismiss: () => void
}

export function useAppUpdate(): UpdateApi {
  const [available, setAvailable] = useState<DeployedBuild | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const check = useCallback(async () => {
    const deployed = await deployedBuild()
    if (deployed) setAvailable(isNewer(deployed) ? deployed : null)
  }, [])

  useEffect(() => {
    void check()

    /*
     * Coming back to the app is the moment worth checking. A deploy that landed
     * while the phone was in a pocket is exactly the update somebody is running
     * without knowing, and `visibilitychange` is the only event a home-screen
     * app or a TWA reliably fires when it is opened again.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', check)
    window.addEventListener('pageshow', onVisible)
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', check)
      window.removeEventListener('pageshow', onVisible)
      window.clearInterval(timer)
    }
  }, [check])

  return { available: dismissed ? null : available, reload: refreshApp, dismiss: () => setDismissed(true) }
}
